// Poll responses may overlap (realtime + timer), arrive newest-first, or contain
// several messages at the same timestamp. Count each event once, not each poll.
export function ensureMessageAccount(storage, keys, email) {
  if (!email || storage.get('ft-message-account') === email) return;
  // Legacy caches have no provable owner. Re-fetch server history instead of
  // showing another account's conversations on a shared device.
  for (const key of keys) {
    if (/^ft-(dm-|unread-|last-read-|last-seen-|reactions-)/.test(key)
      || key === 'ft-last-poll' || key === 'ft-seen-message-events') storage.del(key);
  }
  storage.set('ft-message-account', email);
}

export function applyMessagePoll(storage, result) {
  const stored = storage.get('ft-seen-message-events');
  const seen = new Set(Array.isArray(stored) ? stored : []);
  const fresh = [];
  for (const [kind, rows] of [['dm', result.dms], ['group', result.groups]]) {
    const watermarks = new Map();
    const messages = (Array.isArray(rows) ? rows : []).filter(m =>
      m && (kind === 'dm' ? m.from : m.group) && Number.isFinite(Date.parse(m.ts))
    ).sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts));
    for (const m of messages) {
      const peer = kind === 'dm' ? m.from : `group-${m.group}`;
      const seenKey = `ft-last-seen-${peer}`;
      if (!watermarks.has(peer)) watermarks.set(peer, Date.parse(storage.get(seenKey)) || 0);
      const identity = m.id != null
        ? JSON.stringify([kind, peer, 'id', String(m.id)])
        : JSON.stringify([kind, peer, m.ts, m.text ?? '']);
      if (seen.has(identity)) continue;
      seen.add(identity);
      // A timestamp equal to the watermark may be a second distinct message.
      if (Date.parse(m.ts) < watermarks.get(peer)) continue;
      storage.set(seenKey, m.ts);
      if (kind === 'dm') {
        const key = `ft-dm-${m.from}`;
        const storedMessages = storage.get(key);
        const cached = Array.isArray(storedMessages) ? storedMessages : [];
        const imageData = m.imageData ?? m.data?.imageData ?? null;
        const duplicate = cached.some(c => m.id != null && c.id != null && !String(c.id).startsWith('poll-')
          ? String(c.id) === String(m.id)
          : c.from === m.from && c.ts === m.ts && c.text === m.text);
        if (!duplicate) {
          const merged = [...cached, {
            ...m, id: m.id ?? `poll-${identity}`, imageData, isOwn: false,
          }].sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts));
          // A global poll must not evict unsent drafts created in a conversation.
          const retained = [...merged.filter(c => c.local || c.pending || c.failed || c.status === 'pending' || c.status === 'failed'), ...merged.slice(-200)];
          storage.set(key, [...new Map(retained.map(c => [String(c.id), c])).values()]);
        }
      }
      const lastRead = Date.parse(storage.get(`ft-last-read-${peer}`)) || 0;
      if (Date.parse(m.ts) <= lastRead) continue;
      const key = `ft-unread-${peer}`;
      storage.set(key, Math.max(0, parseInt(storage.get(key), 10) || 0) + 1);
      fresh.push({ ...m, type: kind });
    }
  }
  storage.set('ft-seen-message-events', [...seen].slice(-2000));
  return fresh;
}

export function unreadMessageCount(storage, keys) {
  return keys.filter(key => key.startsWith('ft-unread-'))
    .reduce((sum, key) => sum + Math.max(0, parseInt(storage.get(key), 10) || 0), 0);
}

export function readMessageSnapshot(storage, email, friend, rows) {
  const epoch = '1970-01-01T00:00:00Z';
  const previous = storage.get(`ft-last-read-${friend}`) || epoch;
  const newest = rows.filter(m => !(m.isOwn || m.from === email))
    .reduce((ts, m) => Date.parse(m.ts) > Date.parse(ts) ? m.ts : ts, epoch);
  const readAt = Date.parse(newest) > Date.parse(previous) ? newest : previous;
  const cached = storage.get(`ft-dm-${friend}`);
  const unseen = (Array.isArray(cached) ? cached : [])
    .filter(m => !(m.isOwn || m.from === email) && Date.parse(m.ts) > Date.parse(readAt));
  storage.set(`ft-last-read-${friend}`, readAt);
  storage.set(`ft-unread-${friend}`, unseen.length);
  return Date.parse(readAt) > Date.parse(previous) ? readAt : null;
}
